import { css } from '@emotion/react';
import React, { useContext, useEffect, useState } from 'react';
import { Control, useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { AngularServicesContext } from 'react-app';

import { AdminUser, AdminUserDetails } from 'redux/schemas/models/help-options';
import { CourseUser } from 'redux/schemas/models/my-account';
import { searchCourseUsers } from 'redux/actions/users';

import { halfSpacing } from 'styles/global_defaults/scaffolding';

import TagListAutocomplete from 'institutions/components/help-options-settings/tag-component/tag-list-autocomplete';
import NvIcon from 'shared/components/nv-icon';
import NvRoundButton from 'shared/components/nv-round-button';
import { DailyReportConfig } from './daily-report-configuration-modal';

type SelectAdminsProps = {
  control: Control<DailyReportConfig, object>
};

const SelectAdmins: React.FC<SelectAdminsProps> = ({ control }) => {
  const [availableAdmins, setAvailableAdmins] = useState<AdminUser[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const courseUsersLoaded = useSelector((state) => state.app.courseUsersLoaded);

  const { $state } = useContext(AngularServicesContext);
  const { watch } = useFormContext();
  const dispatch = useAppDispatch();

  const { catalogId } = $state?.params;
  const recipientUserIds = watch('recipientUserIds');

  useEffect(() => {
    const fetchParams = {
      catalogId,
      courseAdmins: true,
      courseLearners: false,
      courseMentors: false,
      filterByRole: null,
      queryTerm: null,
      pageSize: 30,
      pageIndex: 1,
      page: 1,
    };
    dispatch(searchCourseUsers(fetchParams)).then(({ payload }) => {
      const courseUsers: CourseUser[] = payload.response || [];
      const adminUsers = courseUsers.map((courseUser) => ({
        id: courseUser.user.id,
        user: courseUser.user as AdminUserDetails,
      }));
      const selectedAdminIds = recipientUserIds.map((admin) => admin.id);
      const filteredAdmins = adminUsers.filter((admin) => !selectedAdminIds.includes(admin.id));
      setAvailableAdmins(filteredAdmins);
    });
  }, []);

  useEffect(() => {
    if (recipientUserIds.length > 0) {
      const selectedAdminIds = recipientUserIds.map((admin) => admin.id);
      setAvailableAdmins((prevAdmins) => prevAdmins.filter((admin) => !selectedAdminIds.includes(admin.id)));
    }
  }, [recipientUserIds]);

  const {
    fields: selectedAdminFields,
    remove: removeAdmin,
    append: appendAdmin,
  } = useFieldArray<DailyReportConfig, 'recipientUserIds', 'id'>({
    control,
    name: 'recipientUserIds',
    keyName: 'id',
  });

  const onRemoveAdmin = (removedAdmin: AdminUser, index) => {
    // update form
    removeAdmin(index);

    // Add back to available recipients
    const newAvailableAdmins = [...availableAdmins, removedAdmin];
    setAvailableAdmins(newAvailableAdmins);
  };

  const onAddAdmin = (adminId: number) => {
    const recipient = availableAdmins.find((r) => r.id === adminId);

    if (recipient) {
      // update form
      appendAdmin(recipient);

      // Remove from available recipients
      const newAvailableAdmins = availableAdmins.filter((r) => r.id !== adminId);
      setAvailableAdmins(newAvailableAdmins);
    }
    setIsEditing(false);
  };

  return (
    <div className='border border-gray-4 px-3 py-4 mt-4'>
      <div>
        {t.CUSTOM_DOWNLOADS.DAILY_REPORT.MODAL.SELECT_ADMINS()}
        <span className='text-danger ml-1'>*</span>
      </div>
      {!courseUsersLoaded ? (
        <div className='mt-2 gray-2'>
          {t.TIMELINE.LOADING()}
        </div>
      ) : (
        <div className='d-flex flex-wrap mt-2' css={css` gap: ${halfSpacing}px;`}>
          {selectedAdminFields?.map((admin, index) => (
            <div
              className='tag-item d-flex text-small font-weight-bold align-items-center border border-gray-5 bg-gray-7 px-2'
              key={admin.id}
            >
              {admin?.user && (
              <div className='mr-1'>
                {`${admin.user.firstName} ${admin.user.lastName}`}
              </div>
              )}
              <NvIcon
                icon='close'
                size='xss-smallest'
                onClick={() => onRemoveAdmin(admin, index)}
                altLabel={t.FORM.DELETE()}
              />
            </div>
          ))}
          {!isEditing && (
            <NvRoundButton
              icon='icon icon-create-new-post mr-1'
              label={t.FORM.ADD()}
              onClick={() => setIsEditing(true)}
              className='tag-item d-flex text-small font-weight-bold align-items-center border border-gray-5 bg-gray-6 text-primary px-2 ml-0'
              ariaLabel={t.FORM.ADD()}
            />
          )}
          {isEditing && (
            <TagListAutocomplete
              items={availableAdmins}
              onCancel={() => setIsEditing(false)}
              onAdd={onAddAdmin}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SelectAdmins;
